import { apiService } from './api';

export interface UserLaboratoryAccess {
  userId: string;
  accessibleLaboratories: string[];
  permissions: {
    [laboratory: string]: {
      canPrepChemicals: boolean;
      canPerformAnalysis: boolean;
      canValidateResults: boolean;
      canAssignSamples: boolean;
      canViewAllSamples: boolean;
    };
  };
}

export interface LaboratoryDashboardData {
  laboratory: string;
  userAccess: UserLaboratoryAccess;
  samples: any[];
  workflows: any[];
  statistics: {
    totalSamples: number;
    pendingSamples: number;
    inPrepChimique: number;
    inAnalyse: number;
    completedToday: number;
    overdueSamples: number;
    averageTurnaroundHours: number;
    efficiencyPercentage: number;
  };
}

export interface WorkflowAction {
  action: 'start_prep' | 'complete_prep' | 'start_analysis' | 'complete_analysis';
  notes?: string;
  qualityControlPassed?: boolean;
  results?: any;
}

class LaboratoryAccessService {
  private baseUrl = '/laboratory';

  /**
   * Get user's laboratory access permissions
   */
  async getUserAccess(): Promise<UserLaboratoryAccess> {
    try {
      console.log('🔄 LaboratoryAccessService: Getting user access...');
      
      // Try to get from API first
      const response = await apiService.get<UserLaboratoryAccess>(`${this.baseUrl}/access`);
      console.log('✅ LaboratoryAccessService: Got user access from API:', response);
      return response;
    } catch (error) {
      console.warn('⚠️ LaboratoryAccessService: API call failed, creating fallback access:', error);
      
      // Fallback: Get user from localStorage and create access
      const currentUser = this.getCurrentUser();
      return this.createFallbackUserAccess(currentUser);
    }
  }

  /**
   * Get current user from localStorage (auth context)
   */
  private getCurrentUser(): any {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        throw new Error('No user found in localStorage');
      }
      return JSON.parse(userStr);
    } catch (error) {
      console.error('❌ LaboratoryAccessService: Error getting current user:', error);
      return null;
    }
  }

  /**
   * Create fallback user access based on user email/role
   */
  private createFallbackUserAccess(user: any): UserLaboratoryAccess {
    console.log('🔄 Creating fallback access for user:', user);
    
    if (!user) {
      return {
        userId: 'unknown',
        accessibleLaboratories: [],
        permissions: {},
      };
    }
    
    const accessibleLaboratories: string[] = [];
    const permissions: UserLaboratoryAccess['permissions'] = {};

    // Handle specific preparation chimique roles
    if (user.role === 'SAA_PREP_CHIMIQUE' || user.email === 'saa.prep@afrilab.ma') {
      accessibleLaboratories.push('LABORATOIRE_SAA');
      permissions['LABORATOIRE_SAA'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    }
    
    if (user.role === 'ICP_PREP_CHIMIQUE' || user.email === 'icp.prep@afrilab.ma') {
      accessibleLaboratories.push('LABORATOIRE_ICP');
      permissions['LABORATOIRE_ICP'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    }
    
    if (user.role === 'AU_PREP_CHIMIQUE' || user.email === 'au.prep@afrilab.ma') {
      accessibleLaboratories.push('LABORATOIRE_AU');
      permissions['LABORATOIRE_AU'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    }
    
    if (user.role === 'VOLUMETRIE_PREP_CHIMIQUE' || user.email === 'vol.prep@afrilab.ma') {
      accessibleLaboratories.push('LABORATOIRE_VOLUMETRIE');
      permissions['LABORATOIRE_VOLUMETRIE'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    }

    // Handle specific analyst roles
    if (user.role === 'SAA_ANALYST' || user.email?.includes('saa@afrilab.ma') || user.email?.includes('sup.saa@afrilab.ma')) {
      accessibleLaboratories.push('LABORATOIRE_SAA');
      permissions['LABORATOIRE_SAA'] = {
        canPrepChemicals: false,
        canPerformAnalysis: true,
        canValidateResults: user.email?.includes('sup.'),
        canAssignSamples: user.email?.includes('sup.'),
        canViewAllSamples: true,
      };
    }
    
    if (user.role === 'ICP_ANALYST' || user.email?.includes('icp@afrilab.ma') || user.email?.includes('sup.icp@afrilab.ma')) {
      accessibleLaboratories.push('LABORATOIRE_ICP');
      permissions['LABORATOIRE_ICP'] = {
        canPrepChemicals: false,
        canPerformAnalysis: true,
        canValidateResults: user.email?.includes('sup.'),
        canAssignSamples: user.email?.includes('sup.'),
        canViewAllSamples: true,
      };
    }
    
    if (user.role === 'AU_ANALYST' || user.email?.includes('au@afrilab.ma') || user.email?.includes('sup.au@afrilab.ma')) {
      accessibleLaboratories.push('LABORATOIRE_AU');
      permissions['LABORATOIRE_AU'] = {
        canPrepChemicals: false,
        canPerformAnalysis: true,
        canValidateResults: user.email?.includes('sup.'),
        canAssignSamples: user.email?.includes('sup.'),
        canViewAllSamples: true,
      };
    }
    
    if (user.role === 'VOLUMETRIE_ANALYST' || user.email?.includes('volumetrie@afrilab.ma') || user.email?.includes('sup.vol@afrilab.ma')) {
      accessibleLaboratories.push('LABORATOIRE_VOLUMETRIE');
      permissions['LABORATOIRE_VOLUMETRIE'] = {
        canPrepChemicals: false,
        canPerformAnalysis: true,
        canValidateResults: user.email?.includes('sup.'),
        canAssignSamples: user.email?.includes('sup.'),
        canViewAllSamples: true,
      };
    }

    // Handle mechanical preparation
    if (user.role === 'PREPARATION_MECANIQUE' || user.email?.includes('preparation@afrilab.ma')) {
      accessibleLaboratories.push('PREPARATION_MECANIQUE');
      permissions['PREPARATION_MECANIQUE'] = {
        canPrepChemicals: true,
        canPerformAnalysis: false,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: false,
      };
    }
    
    // Handle general roles
    if (user.role === 'LABORATORY_MANAGER' || user.role === 'ADMIN') {
      accessibleLaboratories.push(
        'LABORATOIRE_SAA', 
        'LABORATOIRE_ICP', 
        'LABORATOIRE_AU', 
        'LABORATOIRE_VOLUMETRIE',
        'PREPARATION_MECANIQUE',
        'VALIDATION_SERVICE'
      );
      
      // Full permissions for managers/admins
      const fullPermissions = {
        canPrepChemicals: true,
        canPerformAnalysis: true,
        canValidateResults: true,
        canAssignSamples: true,
        canViewAllSamples: true,
      };
      
      accessibleLaboratories.forEach(lab => {
        permissions[lab] = fullPermissions;
      });
    }
    
    // If no specific lab found but user is analyst, give access to SAA as default
    if (accessibleLaboratories.length === 0 && user.role === 'ANALYST') {
      accessibleLaboratories.push('LABORATOIRE_SAA');
      permissions['LABORATOIRE_SAA'] = {
        canPrepChemicals: true,
        canPerformAnalysis: true,
        canValidateResults: false,
        canAssignSamples: false,
        canViewAllSamples: true,
      };
    }

    // Remove duplicates
    const uniqueLaboratories = [...new Set(accessibleLaboratories)];
    
    return {
      userId: user.id,
      accessibleLaboratories: uniqueLaboratories,
      permissions: permissions,
    };
  }

  /**
   * Get user's laboratory access from samples API
   */
  async getMyLaboratories(): Promise<UserLaboratoryAccess> {
    try {
      return await apiService.get<UserLaboratoryAccess>('/samples/my-laboratories');
    } catch (error) {
      console.error('Error fetching my laboratories:', error);
      return this.getMockUserAccess();
    }
  }

  /**
   * Get laboratory-specific dashboard data
   */
  async getLaboratoryDashboard(laboratory: string): Promise<LaboratoryDashboardData> {
    try {
      // Get samples for this laboratory using the correct endpoint
      const samples = await this.getLaboratorySamples(laboratory);
      
      // Get user access information
      const userAccess = await this.getMyLaboratories();
      
      // Get workflows (fallback to mock if endpoint doesn't exist)
      let workflows = [];
      try {
        workflows = await this.getAccessibleWorkflows(laboratory);
      } catch (error) {
        console.warn('Workflows endpoint not available, using mock data');
        workflows = this.getMockWorkflows(laboratory);
      }
      
      // Calculate statistics from the samples
      const statistics = this.calculateStatisticsFromSamples(samples, laboratory);
      
      return {
        laboratory,
        userAccess,
        samples,
        workflows,
        statistics,
      };
    } catch (error) {
      console.error('Error fetching laboratory dashboard:', error);
      // Return mock data for development
      return this.getMockDashboardData(laboratory);
    }
  }

  /**
   * Calculate statistics from samples data
   */
  private calculateStatisticsFromSamples(samples: any[], laboratory: string) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filter samples for this laboratory based on status
    const laboratorySamples = samples.filter(sample => 
      sample.current_laboratory === laboratory ||
      sample.statut?.includes(laboratory.replace('LABORATOIRE_', '')) ||
      sample.statut?.includes('SAA') && laboratory === 'LABORATOIRE_SAA' ||
      sample.statut?.includes('ICP') && laboratory === 'LABORATOIRE_ICP' ||
      sample.statut?.includes('AU') && laboratory === 'LABORATOIRE_AU' ||
      sample.statut?.includes('VOLUMETRIE') && laboratory === 'LABORATOIRE_VOLUMETRIE'
    );

    const totalSamples = laboratorySamples.length;
    
    // Count samples in different phases
    const inPrepChimique = laboratorySamples.filter(sample => 
      sample.statut?.includes('PREP_CHIMIQUE')
    ).length;
    
    const inAnalyse = laboratorySamples.filter(sample => 
      sample.statut?.includes('ANALYSE') && !sample.statut?.includes('PREP_CHIMIQUE')
    ).length;
    
    const pendingSamples = laboratorySamples.filter(sample => 
      sample.statut === 'RECEPTION' || sample.statut === 'PREPARATION_MECANIQUE'
    ).length;
    
    // Count completed today (samples that moved to validation or completed)
    const completedToday = laboratorySamples.filter(sample => {
      const dateModified = new Date(sample.date_modification || sample.date_reception);
      return dateModified >= today && 
             (sample.statut === 'VALIDATION_SERVICE' || sample.statut === 'PRET');
    }).length;

    // Calculate overdue samples (simplified logic)
    const overdueSamples = laboratorySamples.filter(sample => {
      const receptionDate = new Date(sample.date_reception);
      const hoursElapsed = (Date.now() - receptionDate.getTime()) / (1000 * 60 * 60);
      
      // Different TAT expectations by priority
      const expectedHours = sample.priorite === 'URGENTE' ? 24 : 
                           sample.priorite === 'ELEVEE' ? 72 : 120;
      
      return hoursElapsed > expectedHours && sample.statut !== 'PRET';
    }).length;

    return {
      totalSamples,
      pendingSamples,
      inPrepChimique,
      inAnalyse,
      completedToday,
      overdueSamples,
      averageTurnaroundHours: 36, // Placeholder
      efficiencyPercentage: Math.max(0, Math.round(((totalSamples - overdueSamples) / Math.max(totalSamples, 1)) * 100)),
    };
  }

  /**
   * Get samples accessible to user
   */
  async getAccessibleSamples(laboratory?: string, status?: string): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      if (laboratory) params.append('laboratory', laboratory);
      if (status) params.append('status', status);

      // Use the enhanced samples endpoint with laboratory filtering
      const response = await apiService.get<any>(`/samples?${params.toString()}`);
      return response.samples || response; // Handle both paginated and direct responses
    } catch (error) {
      console.error('Error fetching samples:', error);
      return this.getMockSamples(laboratory);
    }
  }

  /**
   * Get samples for a specific laboratory
   */
  async getLaboratorySamples(laboratory: string): Promise<any[]> {
    try {
      return await apiService.get<any[]>(`/samples/laboratory/${laboratory}`);
    } catch (error) {
      console.error('Error fetching laboratory samples:', error);
      return this.getMockSamples(laboratory);
    }
  }

  /**
   * Get workflows accessible to user
   */
  async getAccessibleWorkflows(laboratory?: string, status?: string): Promise<any[]> {
    try {
      const params = new URLSearchParams();
      if (laboratory) params.append('laboratory', laboratory);
      if (status) params.append('status', status);

      return await apiService.get<any[]>(`/laboratory/workflows?${params.toString()}`);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      return this.getMockWorkflows(laboratory);
    }
  }

  /**
   * Get specific sample
   */
  async getSample(sampleId: string): Promise<any> {
    try {
      // Use the enhanced samples endpoint with access control
      return await apiService.get<any>(`/samples/${sampleId}`);
    } catch (error) {
      console.error('Error fetching sample:', error);
      throw error;
    }
  }

  /**
   * Get specific sample from laboratory endpoint
   */
  async getLaboratorySample(sampleId: string): Promise<any> {
    try {
      return await apiService.get<any>(`/laboratory/samples/${sampleId}`);
    } catch (error) {
      console.error('Error fetching laboratory sample:', error);
      throw error;
    }
  }

  /**
   * Perform workflow action
   */
  async performWorkflowAction(workflowId: string, action: WorkflowAction): Promise<{ success: boolean; message: string }> {
    try {
      return await apiService.post<{ success: boolean; message: string }>(`/laboratory/workflows/${workflowId}/action`, action);
    } catch (error) {
      console.error('Error performing workflow action:', error);
      throw error;
    }
  }

  /**
   * Assign sample to user
   */
  async assignSample(sampleId: string, assigneeId: string, laboratory: string): Promise<{ success: boolean; message: string }> {
    try {
      // Use the enhanced samples endpoint with laboratory access control
      await apiService.patch<any>(`/samples/${sampleId}/assign`, {
        assignedTo: assigneeId,
        laboratory,
      });
      return { success: true, message: 'Sample assigned successfully' };
    } catch (error) {
      console.error('Error assigning sample:', error);
      throw error;
    }
  }

  /**
   * Assign sample via laboratory endpoint
   */
  async assignLaboratorySample(sampleId: string, assigneeId: string, laboratory: string): Promise<{ success: boolean; message: string }> {
    try {
      return await apiService.put<{ success: boolean; message: string }>(`/laboratory/samples/${sampleId}/assign`, {
        assigneeId,
        laboratory,
      });
    } catch (error) {
      console.error('Error assigning laboratory sample:', error);
      throw error;
    }
  }

  /**
   * Get laboratory statistics
   */
  async getLaboratoryStatistics(laboratory: string): Promise<any> {
    try {
      return await apiService.get<any>(`/laboratory/statistics/${laboratory}`);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      return this.getMockStatistics();
    }
  }

  /**
   * Update sample status with access control
   */
  async updateSampleStatus(sampleId: string, status: string, reason?: string): Promise<any> {
    try {
      // Use regular PATCH endpoint instead of /status endpoint due to access control issues
      return await apiService.patch<any>(`/samples/${sampleId}`, {
        statut: status,
        commentaires: reason,
      });
    } catch (error) {
      console.error('Error updating sample status:', error);
      throw error;
    }
  }

  // Mock data methods for development/fallback
  private getMockUserAccess(): UserLaboratoryAccess {
    return {
      userId: 'mock-user-id',
      accessibleLaboratories: ['LABORATOIRE_SAA', 'LABORATOIRE_ICP'],
      permissions: {
        LABORATOIRE_SAA: {
          canPrepChemicals: true,
          canPerformAnalysis: true,
          canValidateResults: false,
          canAssignSamples: false,
          canViewAllSamples: true,
        },
        LABORATOIRE_ICP: {
          canPrepChemicals: true,
          canPerformAnalysis: true,
          canValidateResults: false,
          canAssignSamples: false,
          canViewAllSamples: true,
        },
      },
    };
  }

  private getMockDashboardData(laboratory: string): LaboratoryDashboardData {
    return {
      laboratory,
      userAccess: this.getMockUserAccess(),
      samples: this.getMockSamples(laboratory),
      workflows: this.getMockWorkflows(laboratory),
      statistics: this.getMockStatistics(),
    };
  }

  private getMockSamples(laboratory?: string): any[] {
    return [
      {
        id: '1',
        sample_id: 'AFL-2024-00001',
        client: { company_name: 'Mining Corp Ltd' },
        type_echantillon: 'MINERAI',
        statut: laboratory ? `${laboratory}_PREP_CHIMIQUE` : 'PREPARATION_MECANIQUE',
        current_laboratory: laboratory || 'LABORATOIRE_SAA',
        selected_laboratory_methods: ['AA-PRE.MO/ANA/002', 'AA-PRE.MO/ANA/002 Cu'],
        priorite: 'NORMALE',
        date_reception: new Date().toISOString(),
        progress_percentage: 40,
      },
      {
        id: '2',
        sample_id: 'AFL-2024-00002',
        client: { company_name: 'Gold Mining Inc' },
        type_echantillon: 'CONCENTRE',
        statut: laboratory ? `${laboratory}_ANALYSE` : 'LABORATOIRE_SAA_ANALYSE',
        current_laboratory: laboratory || 'LABORATOIRE_SAA',
        selected_laboratory_methods: ['AA-PRE.MO/ANA/002 Au'],
        priorite: 'URGENTE',
        date_reception: new Date(Date.now() - 86400000).toISOString(),
        progress_percentage: 70,
      },
    ];
  }

  private getMockWorkflows(laboratory?: string): any[] {
    return [
      {
        id: '1',
        sample_id: '1',
        laboratory: laboratory || 'LABORATOIRE_SAA',
        method_codes: ['AA-PRE.MO/ANA/002'],
        status: 'PREP_CHIMIQUE',
        started_at: new Date().toISOString(),
        prep_chimique_started_at: new Date().toISOString(),
        progress_percentage: 30,
        is_overdue: false,
      },
      {
        id: '2',
        sample_id: '2',
        laboratory: laboratory || 'LABORATOIRE_SAA',
        method_codes: ['AA-PRE.MO/ANA/002 Au'],
        status: 'ANALYSE',
        started_at: new Date(Date.now() - 86400000).toISOString(),
        analyse_started_at: new Date(Date.now() - 3600000).toISOString(),
        progress_percentage: 70,
        is_overdue: false,
      },
    ];
  }

  private getMockStatistics() {
    return {
      totalSamples: 15,
      pendingSamples: 3,
      inPrepChimique: 4,
      inAnalyse: 6,
      completedToday: 2,
      overdueSamples: 1,
      averageTurnaroundHours: 36,
      efficiencyPercentage: 92,
    };
  }
}

export const laboratoryAccessService = new LaboratoryAccessService();
export default laboratoryAccessService; 